/**
 * Tool confirmation: edit a campaign's settings.
 *
 * Input shape: {
 *   campaignId: string,
 *   campaignName?: string,
 *   updates: { dailyLimit?, stopOnReply?, autoProspect?, autoSendEmails?, autoReply? }
 * }
 *
 * Renders a preview diff + allows the user to adjust individual toggles
 * before confirming. Not a replacement for the full SettingsTab — this
 * is a focused "here's what the agent wants to change" view.
 */

import { useState } from 'react';
import { CogIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import type { ToolConfirmationProps } from '../tool-registry';

interface Updates {
  dailyLimit?: number;
  stopOnReply?: boolean;
  autoProspect?: boolean;
  autoSendEmails?: boolean;
  autoReply?: boolean;
}

export function EditCampaignSettingsTool({ input, onConfirm, onReject }: ToolConfirmationProps) {
  const campaignName = (input.campaignName as string) || 'this campaign';
  const initialUpdates = (input.updates as Updates) || {};
  const [updates, setUpdates] = useState<Updates>(initialUpdates);

  const fields: { key: keyof Updates; label: string; kind: 'bool' | 'number' }[] = [
    { key: 'autoProspect', label: 'Prospecting Agent', kind: 'bool' },
    { key: 'autoSendEmails', label: 'Auto-send AI-personalised emails', kind: 'bool' },
    { key: 'autoReply', label: 'Auto-reply to responses', kind: 'bool' },
    { key: 'stopOnReply', label: 'Stop on reply', kind: 'bool' },
    { key: 'dailyLimit', label: 'Daily send limit', kind: 'number' },
  ];

  return (
    <div className="h-full flex items-center justify-center p-8">
      <div className="bg-card border border-border rounded-2xl p-6 max-w-lg w-full shadow-lg">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-info-surface flex items-center justify-center">
            <CogIcon className="h-5 w-5 text-info-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Update campaign settings</h2>
            <p className="text-xs text-muted-foreground">{campaignName}</p>
          </div>
        </div>

        <div className="space-y-4">
          {fields
            .filter(f => f.key in initialUpdates)
            .map(f => (
              <div key={f.key} className="flex items-center justify-between">
                <span className="text-sm font-medium">{f.label}</span>
                {f.kind === 'bool' ? (
                  <Switch
                    checked={!!updates[f.key]}
                    onCheckedChange={(v) => setUpdates(u => ({ ...u, [f.key]: v }))}
                  />
                ) : (
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={(updates[f.key] as number) ?? 0}
                    onChange={(e) => setUpdates(u => ({ ...u, [f.key]: parseInt(e.target.value || '0', 10) }))}
                    className="w-24 px-3 py-1.5 text-sm rounded-lg border border-control-border bg-input-bg"
                  />
                )}
              </div>
            ))}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="secondary" onClick={onReject}>Cancel</Button>
          <Button variant="primary" onClick={() => onConfirm({ ...input, updates })}>
            Apply changes
          </Button>
        </div>
      </div>
    </div>
  );
}
